import type {
  CreateGenerationQueueItemRequest,
  EntryOrFreeText,
  GenerationModelKey,
  GenerationQueueItemResponse,
  LandingPageUrlField,
  LibraryEntryResponse,
} from '@malaberg/shared';
import { DEFAULT_GENERATION_MODEL_KEY } from '@malaberg/shared';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLibraries } from '../../hooks/use-libraries';
import { useRuntimeStore } from '../../state/runtime-store';
import { generationModelLabel, generationModelOptions } from '../../utils/generation-models';

interface GenerationQueueItemEditorProps {
  opened: boolean;
  mode: 'edit' | 'requeue';
  item: GenerationQueueItemResponse | null;
  saving: boolean;
  onClose: () => void;
  onSave: (body: CreateGenerationQueueItemRequest) => Promise<unknown> | unknown;
}

interface DraftState {
  generationModelKey: GenerationModelKey;
  productId: string;
  market: string;
  angle: string;
  avatar: string;
  problemMechanism: string;
  solutionMechanism: string;
  ingredients: string;
  awarenessLevel: string;
  landingPageUrl: string;
  optionalGuidance: string;
  winningStructure: string;
  sourceCopy: string;
}

const blankDraft: DraftState = {
  generationModelKey: DEFAULT_GENERATION_MODEL_KEY,
  productId: '',
  market: '',
  angle: '',
  avatar: '',
  problemMechanism: '',
  solutionMechanism: '',
  ingredients: '',
  awarenessLevel: '',
  landingPageUrl: '',
  optionalGuidance: '',
  winningStructure: '',
  sourceCopy: '',
};

const requiredFields: Array<{ key: keyof DraftState; label: string }> = [
  { key: 'productId', label: 'Product ID' },
  { key: 'market', label: 'Market' },
  { key: 'angle', label: 'Angle' },
  { key: 'avatar', label: 'Avatar' },
  { key: 'problemMechanism', label: 'Problem mechanism' },
  { key: 'solutionMechanism', label: 'Solution mechanism' },
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'awarenessLevel', label: 'Awareness level' },
];

export function GenerationQueueItemEditor({
  opened,
  mode,
  item,
  saving,
  onClose,
  onSave,
}: GenerationQueueItemEditorProps) {
  const [draft, setDraft] = useState<DraftState>(blankDraft);
  const [validationError, setValidationError] = useState<string | null>(null);
  const runtimeInfo = useRuntimeStore((state) => state.runtimeInfo);
  const refreshRuntimeInfo = useRuntimeStore((state) => state.refresh);
  const modalProductId = opened ? draft.productId.trim() : '';
  const productScope = modalProductId ? `product:${modalProductId}` : null;
  const awarenessScope = opened ? 'workspace' : null;
  const { entries: angleEntries } = useLibraries('angle', productScope);
  const { entries: avatarEntries } = useLibraries('avatar', productScope);
  const { entries: problemMechanismEntries } = useLibraries('problem_mechanism', productScope);
  const { entries: solutionMechanismEntries } = useLibraries('solution_mechanism', productScope);
  const { entries: ingredientEntries } = useLibraries('ingredient', productScope);
  const { entries: landingPageUrlEntries } = useLibraries('landing_page_url', productScope);
  const { entries: awarenessLevelEntries } = useLibraries('awareness_level', awarenessScope);

  const entryLabelsById = useMemo(
    () =>
      buildEntryLabelLookup([
        angleEntries,
        avatarEntries,
        problemMechanismEntries,
        solutionMechanismEntries,
        ingredientEntries,
        landingPageUrlEntries,
        awarenessLevelEntries,
      ]),
    [
      angleEntries,
      avatarEntries,
      problemMechanismEntries,
      solutionMechanismEntries,
      ingredientEntries,
      landingPageUrlEntries,
      awarenessLevelEntries,
    ]
  );
  const resolveEntryLabel = useCallback(
    (entryId: string) => entryLabelsById.get(entryId),
    [entryLabelsById]
  );

  useEffect(() => {
    if (!opened) return;
    setDraft(item ? draftFromItem(item) : blankDraft);
    setValidationError(null);
  }, [item, opened]);

  useEffect(() => {
    if (!opened || !item) return;
    setDraft((current) => resolveDraftEntryLabels(current, item.brief, resolveEntryLabel));
  }, [item, opened, resolveEntryLabel]);

  useEffect(() => {
    if (opened && !runtimeInfo) {
      void refreshRuntimeInfo();
    }
  }, [opened, refreshRuntimeInfo, runtimeInfo]);

  const generationModelSelectData = generationModelOptions(runtimeInfo).map((option) => ({
    value: option.key,
    label: option.label,
    available: option.available,
  }));

  const briefMode = item?.brief.mode ?? 'use_existing_structure';
  const title =
    mode === 'requeue'
      ? `Edit and requeue ${itemKindNoun(briefMode)}`
      : `Edit queued ${itemKindNoun(briefMode)}`;
  const saveLabel =
    mode === 'requeue'
      ? `Requeue ${itemKindNoun(briefMode)}`
      : `Save queued ${itemKindNoun(briefMode)}`;

  function setDraftField<K extends keyof DraftState>(key: K, value: DraftState[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setValidationError(null);
  }

  async function handleSave() {
    if (!item) return;

    const error = firstValidationError(draft, briefMode);
    if (error) {
      setValidationError(error);
      return;
    }

    await onSave({ brief: buildBrief(item.brief, draft, resolveEntryLabel) });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="lg"
      overlayProps={{ backgroundOpacity: 0.72, blur: 2 }}
    >
      <Stack gap="sm">
        <Group gap="xs">
          <Badge color="gray" variant="light">
            {modeLabel(briefMode)}
          </Badge>
        </Group>

        {validationError && (
          <Alert color="red" variant="light">
            {validationError}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Product ID"
            value={draft.productId}
            onChange={(event) => setDraftField('productId', event.currentTarget.value)}
          />
          <TextInput
            label="Market"
            value={draft.market}
            onChange={(event) => setDraftField('market', event.currentTarget.value)}
          />
          <Stack gap={4}>
            <TextInput
              label="Model"
              readOnly
              value={generationModelLabel(draft.generationModelKey)}
            />
            <Group gap={4} wrap="wrap">
              {generationModelSelectData.map((option) => (
                <Button
                  key={option.value}
                  variant={option.value === draft.generationModelKey ? 'light' : 'subtle'}
                  color="gray"
                  size="xs"
                  disabled={!option.available}
                  aria-pressed={option.value === draft.generationModelKey}
                  onClick={() => {
                    if (!option.available) return;
                    setDraftField('generationModelKey', option.value as GenerationModelKey);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Group>
          </Stack>
          <TextInput
            label="Angle"
            value={draft.angle}
            onChange={(event) => setDraftField('angle', event.currentTarget.value)}
          />
          <TextInput
            label="Avatar"
            value={draft.avatar}
            onChange={(event) => setDraftField('avatar', event.currentTarget.value)}
          />
          <TextInput
            label="Problem mechanism"
            value={draft.problemMechanism}
            onChange={(event) => setDraftField('problemMechanism', event.currentTarget.value)}
          />
          <TextInput
            label="Solution mechanism"
            value={draft.solutionMechanism}
            onChange={(event) => setDraftField('solutionMechanism', event.currentTarget.value)}
          />
          <TextInput
            label="Awareness level"
            value={draft.awarenessLevel}
            onChange={(event) => setDraftField('awarenessLevel', event.currentTarget.value)}
          />
          <TextInput
            label="Landing page URL"
            value={draft.landingPageUrl}
            onChange={(event) => setDraftField('landingPageUrl', event.currentTarget.value)}
          />
        </SimpleGrid>

        <Textarea
          label="Ingredients"
          value={draft.ingredients}
          onChange={(event) => setDraftField('ingredients', event.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Textarea
          label="Optional guidance"
          value={draft.optionalGuidance}
          onChange={(event) => setDraftField('optionalGuidance', event.currentTarget.value)}
          autosize
          minRows={2}
        />
        {briefMode === 'use_existing_structure' && (
          <Textarea
            label="Winning structure"
            value={draft.winningStructure}
            onChange={(event) => setDraftField('winningStructure', event.currentTarget.value)}
            autosize
            minRows={4}
          />
        )}
        {isAdvertorialMode(briefMode) && (
          <Textarea
            label={sourceCopyLabel(briefMode)}
            value={draft.sourceCopy}
            onChange={(event) => setDraftField('sourceCopy', event.currentTarget.value)}
            autosize
            minRows={4}
          />
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving}>
            {saveLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function draftFromItem(
  item: GenerationQueueItemResponse,
  resolveEntryLabel?: EntryLabelResolver
): DraftState {
  return {
    generationModelKey: item.brief.generationModelKey ?? DEFAULT_GENERATION_MODEL_KEY,
    productId: item.brief.productId,
    market: item.brief.market,
    angle: fieldToText(item.brief.angle, resolveEntryLabel),
    avatar: fieldToText(item.brief.avatar, resolveEntryLabel),
    problemMechanism: fieldToText(item.brief.problemMechanism, resolveEntryLabel),
    solutionMechanism: fieldToText(item.brief.solutionMechanism, resolveEntryLabel),
    ingredients: item.brief.ingredients
      .map((ingredient) => fieldToText(ingredient, resolveEntryLabel))
      .join('\n'),
    awarenessLevel: fieldToText(item.brief.awarenessLevel, resolveEntryLabel),
    landingPageUrl: fieldToText(item.brief.landingPageUrl, resolveEntryLabel),
    optionalGuidance: item.brief.optionalGuidance ?? '',
    winningStructure:
      item.brief.mode === 'use_existing_structure' ? item.brief.winningStructure : '',
    sourceCopy: 'sourceCopy' in item.brief ? item.brief.sourceCopy : '',
  };
}

function resolveDraftEntryLabels(
  draft: DraftState,
  original: GenerationQueueItemResponse['brief'],
  resolveEntryLabel: EntryLabelResolver
): DraftState {
  const next: DraftState = {
    ...draft,
    angle: resolveFieldDraftText(draft.angle, original.angle, resolveEntryLabel),
    avatar: resolveFieldDraftText(draft.avatar, original.avatar, resolveEntryLabel),
    problemMechanism: resolveFieldDraftText(
      draft.problemMechanism,
      original.problemMechanism,
      resolveEntryLabel
    ),
    solutionMechanism: resolveFieldDraftText(
      draft.solutionMechanism,
      original.solutionMechanism,
      resolveEntryLabel
    ),
    ingredients: resolveIngredientsDraftText(
      draft.ingredients,
      original.ingredients,
      resolveEntryLabel
    ),
    awarenessLevel: resolveFieldDraftText(
      draft.awarenessLevel,
      original.awarenessLevel,
      resolveEntryLabel
    ),
    landingPageUrl: resolveFieldDraftText(
      draft.landingPageUrl,
      original.landingPageUrl,
      resolveEntryLabel
    ),
  };

  return draftsEqual(draft, next) ? draft : next;
}

function resolveFieldDraftText(
  currentText: string,
  original: EntryOrFreeText | LandingPageUrlField | undefined,
  resolveEntryLabel: EntryLabelResolver
): string {
  if (!original) return currentText;
  return currentText === fieldToText(original)
    ? fieldToText(original, resolveEntryLabel)
    : currentText;
}

function resolveIngredientsDraftText(
  currentText: string,
  original: EntryOrFreeText[],
  resolveEntryLabel: EntryLabelResolver
): string {
  const unresolvedText = original.map((ingredient) => fieldToText(ingredient)).join('\n');
  return currentText === unresolvedText
    ? original.map((ingredient) => fieldToText(ingredient, resolveEntryLabel)).join('\n')
    : currentText;
}

function draftsEqual(left: DraftState, right: DraftState): boolean {
  return (Object.keys(left) as Array<keyof DraftState>).every((key) => left[key] === right[key]);
}

function firstValidationError(
  draft: DraftState,
  mode: GenerationQueueItemResponse['brief']['mode']
): string | null {
  for (const field of requiredFields) {
    if (field.key === 'ingredients') {
      if (ingredientLines(draft.ingredients).length === 0) return 'Ingredients is required.';
      continue;
    }

    if (!draft[field.key].trim()) return `${field.label} is required.`;
  }

  if (mode === 'use_existing_structure' && !draft.winningStructure.trim()) {
    return 'Winning structure is required.';
  }

  if (isAdvertorialMode(mode) && !draft.sourceCopy.trim()) {
    return `${sourceCopyLabel(mode)} is required.`;
  }

  return null;
}

function buildBrief(
  original: GenerationQueueItemResponse['brief'],
  draft: DraftState,
  resolveEntryLabel?: EntryLabelResolver
): CreateGenerationQueueItemRequest['brief'] {
  const landingPageUrl = draft.landingPageUrl.trim();
  const optionalGuidance = draft.optionalGuidance.trim();

  const base = {
    ...original,
    generationModelKey: draft.generationModelKey,
    productId: draft.productId.trim(),
    market: draft.market.trim(),
    angle: toField(original.angle, draft.angle, resolveEntryLabel),
    avatar: toField(original.avatar, draft.avatar, resolveEntryLabel),
    problemMechanism: toField(original.problemMechanism, draft.problemMechanism, resolveEntryLabel),
    solutionMechanism: toField(
      original.solutionMechanism,
      draft.solutionMechanism,
      resolveEntryLabel
    ),
    ingredients: toIngredients(original.ingredients, draft.ingredients, resolveEntryLabel),
    awarenessLevel: toField(original.awarenessLevel, draft.awarenessLevel, resolveEntryLabel),
    ...(landingPageUrl
      ? {
          landingPageUrl: toLandingPageUrlField(
            original.landingPageUrl,
            draft.landingPageUrl,
            resolveEntryLabel
          ),
        }
      : { landingPageUrl: undefined }),
    ...(optionalGuidance ? { optionalGuidance } : { optionalGuidance: undefined }),
  };

  if (original.mode === 'use_existing_structure') {
    return {
      ...base,
      mode: 'use_existing_structure',
      winningStructure: draft.winningStructure.trim(),
    };
  }

  if (isAdvertorialMode(original.mode)) {
    const { winningStructure: _winningStructure, ...withoutWinningStructure } =
      base as typeof base & {
        winningStructure?: string;
      };
    return {
      ...withoutWinningStructure,
      mode: original.mode,
      sourceCopy: draft.sourceCopy.trim(),
    } as CreateGenerationQueueItemRequest['brief'];
  }

  const { winningStructure: _winningStructure, ...withoutWinningStructure } =
    base as typeof base & {
      winningStructure?: string;
    };
  return withoutWinningStructure as CreateGenerationQueueItemRequest['brief'];
}

type EntryLabelResolver = (entryId: string) => string | undefined;

function buildEntryLabelLookup(entrySets: LibraryEntryResponse[][]): Map<string, string> {
  const labels = new Map<string, string>();
  for (const entries of entrySets) {
    for (const entry of entries) {
      labels.set(entry.id, entry.displayName);
    }
  }
  return labels;
}

function toField(
  original: EntryOrFreeText,
  value: string,
  resolveEntryLabel?: EntryLabelResolver
): EntryOrFreeText {
  const trimmed = value.trim();
  return trimmed === fieldToText(original, resolveEntryLabel) ? original : { value: trimmed };
}

function toLandingPageUrlField(
  original: LandingPageUrlField | undefined,
  value: string,
  resolveEntryLabel?: EntryLabelResolver
): LandingPageUrlField {
  const trimmed = value.trim();
  return original && trimmed === fieldToText(original, resolveEntryLabel)
    ? original
    : { value: trimmed };
}

function toIngredients(
  original: EntryOrFreeText[],
  value: string,
  resolveEntryLabel?: EntryLabelResolver
): EntryOrFreeText[] {
  const nextLines = ingredientLines(value);
  const originalLines = original.map((ingredient) => fieldToText(ingredient, resolveEntryLabel));

  if (
    nextLines.length === originalLines.length &&
    nextLines.every((line, index) => line === originalLines[index])
  ) {
    return original;
  }

  return nextLines.map((line) => ({ value: line }));
}

function ingredientLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function fieldToText(
  field?: EntryOrFreeText | LandingPageUrlField | null,
  resolveEntryLabel?: EntryLabelResolver
): string {
  if (!field) return '';
  if ('ephemeralValue' in field) return field.ephemeralValue;
  if ('value' in field) return field.value;
  return resolveEntryLabel?.(field.entryId) ?? `Entry ${field.entryId}`;
}

function modeLabel(mode: GenerationQueueItemResponse['brief']['mode']): string {
  switch (mode) {
    case 'scratch_premise_set':
      return 'Premise set';
    case 'scratch_ad_draft':
      return 'Scratch draft';
    case 'fb_ad_to_advertorial':
      return 'Facebook advertorial';
    case 'competitor_advertorial_to_advertorial':
      return 'Competitor advertorial';
    default:
      return 'Existing structure';
  }
}

function itemKindNoun(mode: GenerationQueueItemResponse['brief']['mode']): string {
  switch (mode) {
    case 'scratch_premise_set':
      return 'premise set';
    case 'scratch_ad_draft':
      return 'scratch draft';
    case 'fb_ad_to_advertorial':
    case 'competitor_advertorial_to_advertorial':
      return 'advertorial';
    default:
      return 'ad';
  }
}

function isAdvertorialMode(
  mode: GenerationQueueItemResponse['brief']['mode']
): mode is 'fb_ad_to_advertorial' | 'competitor_advertorial_to_advertorial' {
  return mode === 'fb_ad_to_advertorial' || mode === 'competitor_advertorial_to_advertorial';
}

function sourceCopyLabel(
  mode: 'fb_ad_to_advertorial' | 'competitor_advertorial_to_advertorial'
): string {
  return mode === 'fb_ad_to_advertorial'
    ? 'Source Facebook ad'
    : 'Source competitor advertorial copy';
}
